import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { subscribeNewsletter } from "../lib/blogApi";

export function NewsletterSignup({ source = "blog", variant = "panel" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    try {
      const r = await subscribeNewsletter(email.trim(), source);
      setState("done");
      toast.success(r.message || "You're subscribed!");
    } catch (err) {
      setState("idle");
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    }
  };

  const dark = variant === "panel";

  return (
    <div
      data-testid="newsletter-signup"
      className={`rounded-[22px] p-7 sm:p-9 ${
        dark ? "bg-[#22301f] text-cream-50" : "bg-white border border-surface-line"
      }`}
    >
      <div className="flex items-center gap-2">
        <Mail className={`w-4 h-4 ${dark ? "text-cream-50/80" : "text-[#1f3d2e]"}`} />
        <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${dark ? "text-cream-50/70" : "text-ink-mute"}`}>
          Newsletter
        </span>
      </div>
      <h3 className={`mt-3 text-xl sm:text-2xl font-extrabold tracking-[-0.02em] ${dark ? "text-cream-50" : "text-ink"}`}>
        New safety guides, straight to your inbox
      </h3>
      <p className={`mt-2 text-[14px] leading-relaxed ${dark ? "text-cream-50/75" : "text-ink-soft/85"}`}>
        We publish fresh medication &amp; skincare guides every week. Get them
        first — no spam, unsubscribe anytime.
      </p>

      {state === "done" ? (
        <div
          data-testid="newsletter-success"
          className={`mt-5 flex items-center gap-2 text-[14px] font-semibold ${dark ? "text-cream-50" : "text-[#1f8f4e]"}`}
        >
          <CheckCircle2 className="w-5 h-5" /> You're on the list — talk soon!
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <input
            type="email"
            required
            data-testid="newsletter-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className={`flex-1 h-12 rounded-full px-5 text-[14px] outline-none ${
              dark
                ? "bg-cream-50/10 text-cream-50 placeholder:text-cream-50/50 border border-cream-50/20 focus:border-cream-50/50"
                : "bg-cream-50 text-ink placeholder:text-ink-mute border border-surface-line focus:border-ink/30"
            }`}
          />
          <button
            type="submit"
            disabled={state === "loading"}
            data-testid="newsletter-submit"
            className={`inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full text-[14px] font-bold transition-all disabled:opacity-60 ${
              dark ? "bg-cream-50 text-ink hover:scale-[1.02]" : "bg-ink text-cream-50 hover:bg-ink-soft"
            }`}
          >
            {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}
    </div>
  );
}
